import React from 'react';
import {
  Box,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import ColoredAlert from '../../global/ColoredAlert';
import SettingsBox from '../SettingsBox';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import usePublishWaiver from '../../../hooks/waivers/usePublishWaiver';
import FullPageModal from '../../modal/FullPageModal';
import StaticLiabilityWaiverForm from '../StaticLiabilityWaiverForm';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import { WaiverSettingsType } from '../../../types/waivers';
import AcknowledgmentsForm from '../AcknowledgmentsForm';
import MedicalHistoryForm from '../MedicalHistoryForm';
import { FooterAction, LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import useSelectWaiver from '../../../hooks/waivers/useSelectWaiver';
import DropdownMenu from '../../global/DropdownMenu';
import { previewWaiver } from '../../../helpers/waivers';
import IDUploadForm from '../IDUploadForm';
import EmployeeForm from '../EmployeeForm';

const DEFAULT_SPACING = 2.5;
const buttonStyle: SxProps<Theme> = {
  paddingX: 7,
  paddingY: 1.5,
};

function WaiverRequirementsSettings() {
  const { user } = useUser<LoadedUser>();
  const theme = useTheme();

  const { waiver: selectedWaiver } = useSelectWaiver();
  const { data, isLoading, isFetching, isPlaceholderData } = useGetWaivers({
    waiverName: selectedWaiver?.waiverName,
  });
  const disableActions = isLoading || !data?.draft;

  const [enabledState, setEnabledState] = React.useState<{
    static: boolean;
    identification: boolean;
    acknowledgments: boolean;
    medical: boolean;
    address: boolean;
    employee: boolean;
  }>({
    static: !!data?.draft?.staticWaiverSettings?.enabled,
    identification: !!data?.draft?.identificationSettings?.enabled,
    acknowledgments: !!data?.draft?.acknowledgmentSettings?.enabled,
    medical: !!data?.draft?.medicalHistorySettings?.enabled,
    employee: !!data?.draft?.employeeSettings?.enabled,
    address: !!data?.draft?.collectAddress,
  });
  const [modalType, setModalType] = React.useState<WaiverSettingsType | null>(
    null,
  );
  const [newWaiverForm, setNewWaiverForm] = React.useState<FormData | null>(
    null,
  );

  const { mutation, isPending } = usePublishWaiver({
    draft: data?.draft,
    onSuccess: () => {
      toast.success('Waiver published successfully');
    },
  });
  const { mutation: updateMutation, isPending: isPendingUpdate } =
    useUpdateWaiver({
      draft: data?.draft,
      onError: () => {
        setEnabledState({
          static: !!data?.draft?.staticWaiverSettings?.enabled,
          identification: !!data?.draft?.identificationSettings?.enabled,
          acknowledgments: !!data?.draft?.acknowledgmentSettings?.enabled,
          medical: !!data?.draft?.medicalHistorySettings?.enabled,
          employee: !!data?.draft?.employeeSettings?.enabled,
          address: !!data?.draft?.collectAddress,
        });
      },
    });

  const { draft, published } = data || {};

  const changesNotSaved =
    !isLoading &&
    !isPlaceholderData &&
    (!published ||
      !draft ||
      !draft.updatedAt ||
      !published.publishedAt ||
      draft.updatedAt > published.publishedAt);

  const canPublish = !disableActions && changesNotSaved;

  React.useEffect(() => {
    setEnabledState({
      static: !!data?.draft?.staticWaiverSettings?.enabled,
      identification: !!data?.draft?.identificationSettings?.enabled,
      acknowledgments: !!data?.draft?.acknowledgmentSettings?.enabled,
      medical: !!data?.draft?.medicalHistorySettings?.enabled,
      employee: !!data?.draft?.employeeSettings?.enabled,
      address: !!data?.draft?.collectAddress,
    });
  }, [
    data?.draft?.acknowledgmentSettings?.enabled,
    data?.draft?.identificationSettings?.enabled,
    data?.draft?.medicalHistorySettings?.enabled,
    data?.draft?.staticWaiverSettings?.enabled,
    data?.draft?.employeeSettings?.enabled,
    data?.draft?.collectAddress,
  ]);

  const onPublish = async () => {
    if (!canPublish) return;
    mutation.mutate();
  };

  const onToggle = async (type: WaiverSettingsType) => {
    if (isFetching || isPendingUpdate || isPlaceholderData) return;
    const payload: Parameters<typeof updateMutation.mutate>[0] = {};
    if (type === 'static') {
      payload.staticWaiverSettings = {
        enabled: !draft?.staticWaiverSettings?.enabled,
      };
    } else if (type === 'identification') {
      payload.identificationSettings = {
        enabled: !draft?.identificationSettings?.enabled,
      };
    } else if (type === 'acknowledgments') {
      payload.acknowledgmentSettings = {
        enabled: !draft?.acknowledgmentSettings?.enabled,
      };
    } else if (type === 'medical') {
      payload.medicalHistorySettings = {
        enabled: !draft?.medicalHistorySettings?.enabled,
      };
    } else if (type === 'employee') {
      payload.employeeSettings = {
        enabled: !draft?.employeeSettings?.enabled,
      };
    } else if (type === 'address') {
      payload.collectAddress = !draft?.collectAddress;
    }
    updateMutation.mutate(payload);
    setEnabledState(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const getModalTitle = () => {
    switch (modalType) {
      case 'static':
        return 'Static Liability Waiver';
      case 'acknowledgments':
        return 'Acknowledgments';
      case 'medical':
        return 'Medical History';
      case 'identification':
        return 'ID Upload';
      default:
        return '';
    }
  };

  return (
    <>
      <Stack>
        <Stack spacing={5} pt={3}>
          <Box
            mt={0}
            display="flex"
            alignItems="center"
            flexDirection={{
              xs: 'column',
              sm: 'row',
            }}
            gap={DEFAULT_SPACING}>
            <ColoredAlert
              dismissible={false}
              type="info"
              title="Build your waiver"
              subtitle="You can enable as many of the waiver sections as you want. Once you publish, we’ll automatically send clients a link to complete & sign the waiver on the morning of their appointment and remind them 1 hour before if they have not yet completed it."
            />
            <Stack
              spacing={0.5}
              width={{
                xs: '100%',
                sm: 'auto',
              }}>
              {!isLoading && (
                <Typography
                  variant="body2"
                  color={theme.palette.medGrey.main}
                  textAlign={{
                    sx: 'left',
                    md: 'center',
                  }}>
                  {published?.publishedAt
                    ? `Last published on ${format(
                        new Date(published?.publishedAt),
                        'MM/dd/yy',
                      )}`
                    : 'Not published yet'}
                </Typography>
              )}
              {isLoading && (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={16}
                  sx={{ borderRadius: 1 }}
                />
              )}
              <LoadingButton
                variant="base"
                sx={buttonStyle}
                disabled={
                  disableActions || isFetching || isPending || !canPublish
                }
                loading={isPending}
                onClick={onPublish}>
                <Typography variant="inherit" whiteSpace="nowrap">
                  Publish Waiver
                </Typography>
              </LoadingButton>
              <DropdownMenu
                button={{
                  variant: 'light',
                  keepDefaultStyles: true,
                  sx: buttonStyle,
                  content: (
                    <Typography variant="inherit" whiteSpace="nowrap">
                      Preview
                    </Typography>
                  ),
                }}
                options={
                  [
                    !!published?.publishedAt && {
                      label: 'Preview published waiver',
                      action: () => {
                        previewWaiver(user?.business, published?.id);
                      },
                    },
                    !!draft && {
                      label: 'Preview draft waiver',
                      action: () => {
                        previewWaiver(user?.business, draft?.id);
                      },
                    },
                  ].filter(Boolean) as FooterAction[]
                }
                disabled={
                  disableActions ||
                  isFetching ||
                  (!draft && !published?.publishedAt)
                }
              />
              {changesNotSaved && (
                <Typography
                  variant="body2"
                  textAlign={{
                    sx: 'left',
                    md: 'center',
                  }}
                  color={theme.palette.primary.main}>
                  Changes not published!
                </Typography>
              )}
            </Stack>
          </Box>
          <SettingsBox
            title="Static Liability Waiver"
            description="Upload a document for clients to sign. This should be enabled if you have a waiver that needs a signature, but doesn’t require other client input or acknowledgments."
            button={{
              text: 'Upload Static Waiver',
              onClick: () => setModalType('static'),
              disabled: disableActions,
              isLoading: false,
            }}
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.static,
              onChange: () => onToggle('static'),
              disabled: disableActions,
            }}
          />
          <SettingsBox
            title="Acknowledgments"
            description="Add acknowledgments that clients must agree to. This should be enabled if you prefer clients to physically check a box to acknowledge a list of items before their appointment."
            button={{
              text: 'Edit Acknowledgments',
              onClick: () => setModalType('acknowledgments'),
              disabled: disableActions,
              isLoading: false,
            }}
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.acknowledgments,
              onChange: () => onToggle('acknowledgments'),
              disabled: disableActions,
            }}
          />
          <SettingsBox
            title="Medical History"
            description="Add a list of medical conditions for clients to declare. This should be enabled if you want clients to be shown a list of medical conditions they can declare before their appointment."
            button={{
              text: 'Edit Medical History',
              onClick: () => setModalType('medical'),
              disabled: disableActions,
              isLoading: false,
            }}
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.medical,
              onChange: () => onToggle('medical'),
              disabled: disableActions,
            }}
          />
          <SettingsBox
            title="ID Upload(s)"
            description="Add a section of your waiver for clients to upload a photo of their ID or passport."
            button={{
              text: 'Edit ID Upload Settings',
              onClick: () => setModalType('identification'),
              disabled: disableActions,
              isLoading: false,
            }}
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.identification,
              onChange: () => onToggle('identification'),
              disabled: disableActions,
            }}
          />

          <SettingsBox
            title="Service Provider Section"
            description="Add a section to the waiver for service providers to complete before the waiver is finalized, like ink lot #'s, a signature, tattoo or piercing details, etc. After the customer completes the waiver, providers will get a text to complete their portion from the mobile app."
            button={{
              text: 'Edit Provider Settings',
              onClick: () => setModalType('employee'),
              disabled: disableActions,
              isLoading: false,
            }}
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.employee,
              onChange: () => onToggle('employee'),
              disabled: disableActions,
            }}
          />

          <SettingsBox
            title="Address Collection"
            description="Collect clients physical mailing address when they complete the waiver."
            toggle={{
              hidden: !data?.draft,
              checked: enabledState.address,
              onChange: () => onToggle('address'),
              disabled: disableActions,
            }}
          />
        </Stack>
      </Stack>
      <FullPageModal
        modalOpen={!!modalType}
        setModalOpen={() => setModalType(null)}
        name={getModalTitle()}>
        {modalType === 'static' ? (
          <StaticLiabilityWaiverForm
            draft={data?.draft}
            onSuccess={() => setModalType(null)}
          />
        ) : modalType === 'acknowledgments' ? (
          <AcknowledgmentsForm
            draft={data?.draft}
            onSuccess={() => setModalType(null)}
          />
        ) : modalType === 'identification' ? (
          <IDUploadForm
            draft={data?.draft}
            onSuccess={() => setModalType(null)}
          />
        ) : modalType === 'employee' ? (
          <EmployeeForm
            draft={data?.draft}
            onSuccess={() => setModalType(null)}
          />
        ) : (
          <MedicalHistoryForm
            draft={data?.draft}
            onSuccess={() => setModalType(null)}
          />
        )}
      </FullPageModal>
      <FormSmallModal
        form={newWaiverForm}
        open={!!newWaiverForm}
        onClose={() => setNewWaiverForm(null)}
        alert={{
          title: 'New waiver',
          subtitle:
            'Create a new waiver if you need to have clients sign different waivers for different services.',
          dismissible: false,
          type: 'info',
        }}
      />
    </>
  );
}

export default WaiverRequirementsSettings;
